'use client'

import { signIn, getProviders, SignInResponse, ClientSafeProvider } from 'next-auth/react';
import { useEffect, useState } from 'react';

import { Container, Box, CircularProgress } from '@mui/material';
import { useSearchParams } from 'next/navigation';
import { SignInComponent } from '../components/SignInComponent';
import { CredentialsComponent } from '../components/CredentialsComponent';
import { useMsal } from '@azure/msal-react';
import { BrowserConfigurationAuthError } from '@azure/msal-browser';


function SignInPage() {
  const [providers, setProviders] = useState<Record<string, ClientSafeProvider> | null>(null)
  const [loading, setLoading] = useState<string | null>(null)
  const [showCredentials, setShowCredentials] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const searchParams = useSearchParams()
  const error = searchParams.get('error')

  const { instance } = useMsal();

  useEffect(() => {
    const setupProviders = async () => {
      const res = await getProviders()
      setProviders(res)
    }
    setupProviders()
  }, [])

  const handleSignIn = async (providerId: string) => {
    setFormError(null);
    if (providerId === 'credentials') {
      setShowCredentials(true);
      return;
    }
    setLoading(providerId);

    if (providerId === 'azure-ad' && instance) {
      try {
        // 1. MSAL login for Graph token
        const loginResponse = await instance.loginPopup({
          scopes: ['User.Read', "User.ReadBasic.All"],
        });
        if (!loginResponse.account) {
          setFormError('Microsoft login failed');
          setLoading(null);
          return;
        }
      } catch (popupError) {
        if ((popupError as BrowserConfigurationAuthError)?.errorCode === 'user_cancelled') {
          console.warn('User cancelled Microsoft login popup');
          setLoading(null);
          return;
        }
        console.error('Popup login failed:', popupError);
        setFormError('Microsoft login failed');
        setLoading(null);
        return;
      }

      // 2. NextAuth sign-in
      try {
        const rawCallbackUrl = searchParams.get('callbackUrl');
        const callbackUrl = rawCallbackUrl && !rawCallbackUrl.includes('/auth/signin')
          ? rawCallbackUrl
          : '/';
        await signIn('azure-ad', { callbackUrl, prompt: 'none' });
      } catch (error) {
        setFormError('Microsoft login error');
        console.error('MSAL/NextAuth error:', error);
      } finally {
        setLoading(null);
      }
      return;
    }

    try {
      const rawCallbackUrl = searchParams.get('callbackUrl');
      const callbackUrl = rawCallbackUrl && !rawCallbackUrl.includes('/auth/signin')
        ? rawCallbackUrl
        : '/';
      await signIn(providerId, { callbackUrl });
    } catch (error) {
      setFormError('Sign in error');
      console.error('Sign in error:', error);
    } finally {
      setLoading(null);
    }
  }

  const handleCredentialsSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    setLoading('credentials');
    const result = await signIn('credentials', {
      redirect: false,
      email,
      password,
      callbackUrl: searchParams.get('callbackUrl') || '/'
    }) as SignInResponse | undefined;
    if (result?.error) {
      setFormError(result.error);
      setLoading(null);
    } else if (result?.ok && result.url) {
      // Full-page navigation (not a soft router.push): the credentials sign-in
      // sets the session cookie client-side, but a soft navigation renders the
      // target page on the server before that cookie is sent, so requireAuth()
      // sees no session and bounces back to /auth/signin. A full load makes the
      // server pick up the new cookie on the first landing.
      window.location.assign(result.url);
    }
  }

  if (!providers) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="sm">
      {showCredentials ? (
        <CredentialsComponent
          email={email}
          password={password}
          loading={loading === 'credentials'}
          error={error || formError}
          onEmailChange={setEmail}
          onPasswordChange={setPassword}
          onSubmit={handleCredentialsSubmit}
          onBack={() => setShowCredentials(false)}
        />
      ) : (
        <SignInComponent
          providers={providers}
          loading={loading}
          error={error || formError}
          onSignIn={handleSignIn}
        />
      )}
    </Container>
  )
}


export default SignInPage;
